"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import React, { useState } from "react";
import { AVATAR_SIZE, isPreSizedAvatar, sizedAvatar } from "@/utils/avatar";
interface DoctorAvatarProps {
  src?: string | null;
  name: string;
  /** Rendered size in px; scales the initials and the next/image `sizes` hint. */
  size: number;
  /** Tailwind size classes for the box, e.g. "w-16 h-16". */
  className?: string;
  priority?: boolean;
}

// Soft iOS-style gradients; a name always maps to the same one.
const GRADIENTS = ["from-[#6CB4FF] to-[#2F7DF6]",
// blue
"from-[#8E8CFF] to-[#5E5CE6]",
// indigo
"from-[#5FD4E8] to-[#2AA5C0]",
// teal
"from-[#5BD48A] to-[#2BA45A]",
// green
"from-[#FFBE5C] to-[#F59A1B]",
// orange
"from-[#FF7D9A] to-[#F2476A]",
// pink
"from-[#D69BFF] to-[#A55CE0]" // purple
];
const TITLES = /^(dr\.?|prof\.?|mr\.?|mrs\.?|ms\.?|دکتر|د\.|پروفسور|دکتۆر)$/i;

/** "Dr. Sara Ahmadi" -> "SA", "دکتر میلاد خواجه پور" -> "م‌پ"; "" when there is nothing usable. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(word => word && !TITLES.test(word)).map(word => Array.from(word).find(ch => /\p{L}/u.test(ch))).filter((ch): ch is string => Boolean(ch));
  if (words.length === 0) return "";
  const picked = words.length === 1 ? [words[0]] : [words[0], words[words.length - 1]];
  // ZWNJ keeps Arabic-script letters from joining into one glyph; invisible elsewhere
  return picked.map(ch => ch.toLocaleUpperCase()).join("‌");
}
const gradientFor = (name: string) => {
  let hash = 0;
  for (const ch of name) hash = hash * 31 + ch.codePointAt(0)! >>> 0;
  return GRADIENTS[hash % GRADIENTS.length];
};

/**
 * Doctor photo, or — when there is none or it fails to load — the doctor's
 * initials on a name-seeded gradient (a person glyph if the name has no letters).
 */
const DoctorAvatar: React.FC<DoctorAvatarProps> = t0 => {
  const $ = _c(11);
  const {
    src,
    name,
    size,
    className: t1,
    priority: t2
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const priority = t2 === undefined ? false : t2;
  const [failedSrc, setFailedSrc] = useState(null);
  const url = src ? sizedAvatar(src, AVATAR_SIZE.card) : null;
  const showPhoto = url && failedSrc !== url;
  const box = `relative shrink-0 rounded-full overflow-hidden ring-1 ring-black/[0.06] dark:ring-white/10 ${className}`;
  if (showPhoto) {
    return <div className={`${box} bg-black/[0.04] dark:bg-white/[0.06]`}><Image src={url} alt={name} fill={true} sizes={`${size}px`} className="object-cover" unoptimized={isPreSizedAvatar(url)} priority={priority} onError={() => setFailedSrc(url)} /></div>;
  }
  let t3;
  if ($[0] !== name) {
    t3 = initialsOf(name);
    $[0] = name;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const initials = t3;
  let t4;
  if ($[2] !== name) {
    t4 = gradientFor(name);
    $[2] = name;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = `${box} flex items-center justify-center bg-gradient-to-b ${t4} text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.3)]`;
  let t6;
  if ($[4] !== initials || $[5] !== size) {
    t6 = initials ? <span aria-hidden="true" dir="auto" className="font-semibold leading-none tracking-tight select-none" style={{
      fontSize: Math.round(size * 0.38)
    }}>{initials}</span> : <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{
      width: size * 0.5,
      height: size * 0.5
    }} className="opacity-90"><path d="M12 12a4.5 4.5 0 100-9 4.5 4.5 0 000 9zm0 2c-4.4 0-8 2.24-8 5v1a1 1 0 001 1h14a1 1 0 001-1v-1c0-2.76-3.6-5-8-5z" /></svg>;
    $[4] = initials;
    $[5] = size;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== name || $[8] !== t5 || $[9] !== t6) {
    t7 = <div role="img" aria-label={name} className={t5}>{t6}</div>;
    $[7] = name;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
};
export default DoctorAvatar;